import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const SERVICE_CATEGORIES = [
  'HVAC',
  'ELECTRICAL',
  'PLUMBING',
  'ELEVATOR',
  'GENERATOR',
  'CARPENTRY',
  'GENERAL'
];

const emptyForm = {
  vendor_code: '',
  vendor_name: '',
  contact_person: '',
  phone: '',
  email: '',
  address: '',
  service_categories: [],
  gst_number: '',
  is_active: true
};

function MaintVendors({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = () => ({
    Authorization: 'Bearer ' + token,
    'x-chain-id': selectedProperty?.chain_id,
    'x-property-id': selectedProperty?.id
  });

  const loadRows = async () => {
    if (!selectedProperty?.chain_id || !selectedProperty?.id) return setRows([]);
    try {
      const res = await fetch('/api/v1/maintenance/vendors', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load vendors');
      setRows(Array.isArray(data) ? data : (data.rows || []));
    } catch {
      setError('Network error loading vendors');
    }
  };

  useEffect(() => { loadRows(); }, [selectedProperty?.chain_id, selectedProperty?.id, token]);

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setError('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      vendor_code: row.vendor_code || '',
      vendor_name: row.vendor_name || '',
      contact_person: row.contact_person || '',
      phone: row.phone || '',
      email: row.email || '',
      address: row.address || '',
      service_categories: Array.isArray(row.service_categories) ? row.service_categories : [],
      gst_number: row.gst_number || '',
      is_active: row.is_active !== false
    });
    setEditingId(row.id);
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setError('');
  };

  const toggleCategory = (cat) => {
    setForm((prev) => {
      const cats = prev.service_categories.includes(cat)
        ? prev.service_categories.filter((c) => c !== cat)
        : [...prev.service_categories, cat];
      return { ...prev, service_categories: cats };
    });
  };

  const saveVendor = async () => {
    setError('');
    if (!selectedProperty?.chain_id || !selectedProperty?.id) return setError('Select a property first');
    if (!form.vendor_code.trim()) return setError('Vendor Code is required');
    if (!form.vendor_name.trim()) return setError('Vendor Name is required');

    setSaving(true);
    try {
      const url = editingId
        ? `/api/v1/maintenance/vendors/${editingId}`
        : '/api/v1/maintenance/vendors';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to save vendor');
        return;
      }
      closeModal();
      loadRows();
    } catch {
      setError('Network error saving vendor');
    } finally {
      setSaving(false);
    }
  };

  /* ── styles ── */
  const s = {
    page: { padding: isMobile ? 12 : 24, fontFamily: 'Inter, system-ui, sans-serif', color: '#1e293b' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, margin: 0 },
    btnPrimary: {
      background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px',
      fontWeight: 600, cursor: 'pointer', fontSize: 14
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: {
      textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0',
      background: '#f8fafc', fontWeight: 600, fontSize: 13, color: '#475569', whiteSpace: 'nowrap'
    },
    td: { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
    chip: {
      display: 'inline-block', background: '#e0e7ff', color: '#3730a3', borderRadius: 9999,
      padding: '2px 10px', fontSize: 11, fontWeight: 600, marginRight: 4, marginBottom: 2
    },
    activeBadge: (active) => ({
      display: 'inline-block', borderRadius: 9999, padding: '2px 10px', fontSize: 11, fontWeight: 600,
      background: active ? '#dcfce7' : '#fee2e2', color: active ? '#166534' : '#991b1b'
    }),
    editBtn: {
      background: 'none', border: '1px solid #cbd5e1', borderRadius: 5, padding: '4px 12px',
      cursor: 'pointer', fontSize: 13, color: '#334155'
    },
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 9000
    },
    modal: {
      background: '#fff', borderRadius: 10, padding: isMobile ? 16 : 28, width: isMobile ? '100%' : 520, maxWidth: isMobile ? '95vw' : undefined, maxHeight: '90vh',
      overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
    },
    modalTitle: { fontSize: 18, fontWeight: 700, marginTop: 0, marginBottom: 18 },
    label: { display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#334155' },
    input: {
      width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6,
      fontSize: 14, marginBottom: 14, boxSizing: 'border-box'
    },
    textarea: {
      width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6,
      fontSize: 14, marginBottom: 14, boxSizing: 'border-box', minHeight: 60, resize: 'vertical'
    },
    checkRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
    checkLabel: {
      display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer',
      background: '#f1f5f9', borderRadius: 6, padding: '4px 10px'
    },
    checkLabelActive: {
      display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer',
      background: '#dbeafe', borderRadius: 6, padding: '4px 10px', fontWeight: 600, color: '#1d4ed8'
    },
    footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
    btnCancel: {
      background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 6,
      padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14
    },
    errBox: {
      background: '#fef2f2', color: '#991b1b', padding: '8px 14px', borderRadius: 6,
      marginBottom: 12, fontSize: 13, fontWeight: 500
    },
    empty: { textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 15 }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>Vendor Master</h2>
        <button style={s.btnPrimary} onClick={openCreate}>+ Add Vendor</button>
      </div>

      {error && !showModal && <div style={s.errBox}>{error}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Vendor Code</th>
              <th style={s.th}>Vendor Name</th>
              <th style={s.th}>Contact Person</th>
              <th style={s.th}>Phone</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Service Categories</th>
              <th style={s.th}>Active</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} style={s.empty}>No vendors found. Click "+ Add Vendor" to create one.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} style={{ background: r.is_active === false ? '#fafafa' : undefined }}>
                <td style={s.td}>{r.vendor_code}</td>
                <td style={{ ...s.td, fontWeight: 600 }}>{r.vendor_name}</td>
                <td style={s.td}>{r.contact_person || '-'}</td>
                <td style={s.td}>{r.phone || '-'}</td>
                <td style={s.td}>{r.email || '-'}</td>
                <td style={s.td}>
                  {(Array.isArray(r.service_categories) ? r.service_categories : []).map((cat) => (
                    <span key={cat} style={s.chip}>{cat}</span>
                  ))}
                  {(!r.service_categories || r.service_categories.length === 0) && '-'}
                </td>
                <td style={s.td}>
                  <span style={s.activeBadge(r.is_active !== false)}>
                    {r.is_active !== false ? 'Yes' : 'No'}
                  </span>
                </td>
                <td style={s.td}>
                  <button style={s.editBtn} onClick={() => openEdit(r)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{editingId ? 'Edit Vendor' : 'Create Vendor'}</h3>

            {error && <div style={s.errBox}>{error}</div>}

            <label style={s.label}>Vendor Code *</label>
            <input
              style={s.input}
              value={form.vendor_code}
              onChange={(e) => setForm({ ...form, vendor_code: e.target.value })}
              placeholder="e.g. VND-001"
            />

            <label style={s.label}>Vendor Name *</label>
            <input
              style={s.input}
              value={form.vendor_name}
              onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
              placeholder="Vendor / company name"
            />

            <label style={s.label}>Contact Person</label>
            <input
              style={s.input}
              value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
            />

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Phone</label>
                <input
                  style={s.input}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Email</label>
                <input
                  style={s.input}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>

            <label style={s.label}>Address</label>
            <textarea
              style={s.textarea}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              rows={3}
            />

            <label style={s.label}>Service Categories</label>
            <div style={s.checkRow}>
              {SERVICE_CATEGORIES.map((cat) => {
                const checked = form.service_categories.includes(cat);
                return (
                  <label key={cat} style={checked ? s.checkLabelActive : s.checkLabel}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCategory(cat)}
                      style={{ accentColor: '#2563eb' }}
                    />
                    {cat}
                  </label>
                );
              })}
            </div>

            <label style={s.label}>GST Number</label>
            <input
              style={s.input}
              value={form.gst_number}
              onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
              placeholder="e.g. 22AAAAA0000A1Z5"
            />

            <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                style={{ accentColor: '#2563eb' }}
              />
              Is Active
            </label>

            <div style={s.footer}>
              <button style={s.btnCancel} onClick={closeModal}>Cancel</button>
              <button style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={saveVendor} disabled={saving}>
                {saving ? 'Saving...' : (editingId ? 'Update Vendor' : 'Create Vendor')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintVendors;
